import * as color from 'color'
import * as React from 'react'

import { Terrain } from '../../engine/map'
import * as svg from '../utils/svg'
import * as iso from './iso'

const WALL_HEIGHT = iso.HEX_SIZE * 2
const HEX_STROKE = '#101114'

interface IPalette {
  ground: string
  forest: string
  water: string
  wall: string
  pit: string
  accent: string
}

const PALETTES: {[idx: string]: IPalette} = {
  plains: {
    ground: '#6f8d3c',
    forest: '#3f672f',
    water: '#426f7f',
    wall: '#87775d',
    pit: '#31271f',
    accent: '#d6c66b',
  },
  forest: {
    ground: '#315106',
    forest: '#173f18',
    water: '#254e56',
    wall: '#5a5240',
    pit: '#171b12',
    accent: '#92b35a',
  },
  desert: {
    ground: '#b9964b',
    forest: '#7a733d',
    water: '#4f7d85',
    wall: '#80663a',
    pit: '#5d4228',
    accent: '#e2c66e',
  },
  barren: {
    ground: '#605b52',
    forest: '#4f5544',
    water: '#4b5b63',
    wall: '#3f3b35',
    pit: '#20201f',
    accent: '#a08b6e',
  },
  volcano: {
    ground: '#3c3330',
    forest: '#463b33',
    water: '#9e2e20',
    wall: '#181615',
    pit: '#b83428',
    accent: '#efbf1b',
  },
}

function makeTerrainStyle(fill, height = WALL_HEIGHT) {
  return {
    fill,
    height,
    tile: {
      fill,
      stroke: HEX_STROKE,
      strokeWidth: '.05%',
    },
    eastWall: { fill: color(fill).darken(.7).string() },
    southWall: { fill: color(fill).darken(.6).string() },
    westWall: { fill: color(fill).darken(.5).string() },
  }
}

function wallPoints(a, b, height: number) {
  return svg.points(
    { x: a.x, y: a.y },
    { x: a.x, y: a.y + height },
    { x: b.x, y: b.y + height },
    { x: b.x, y: b.y },
  )
}

function terrainFill(palette: IPalette, terrain: Terrain) {
  return terrain === Terrain.Pit ? palette.pit : palette.ground
}

function terrainHeight() {
  return WALL_HEIGHT
}

function terrainOverlayOpacity() {
  return 0
}

function tileAssetPath(themeId: string, variant: number) {
  const theme = PALETTES[themeId] ? themeId : 'plains'
  const index = Math.abs(variant % 10) + 1
  const tile = `${theme}-${index < 10 ? '0' : ''}${index}.svg`
  return `/static/tiles/themes/${theme}/${tile}`
}

function renderRaisedTile(
  themeId: string,
  terrain: Terrain,
  palette: IPalette,
  variant: number,
  tileId: string,
) {
  const fill = terrainFill(palette, terrain)
  const style = makeTerrainStyle(fill, terrainHeight())
  const topFill = variant % 2 === 0
    ? fill
    : color(fill).darken(.03).string()
  const clipId = `theme-tile-image-${tileId}`
  const assetPath = tileAssetPath(themeId, variant)
  const overlayOpacity = terrainOverlayOpacity()

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <polygon points={iso.drawHex(1)} />
        </clipPath>
      </defs>
      <polygon
        points={wallPoints(iso.HEX_POINTS.sw, iso.HEX_POINTS.se, style.height)}
        fill={style.southWall.fill}
      />
      <polygon
        points={wallPoints(iso.HEX_POINTS.sw, iso.HEX_POINTS.w, style.height)}
        fill={style.westWall.fill}
      />
      <polygon
        points={wallPoints(iso.HEX_POINTS.se, iso.HEX_POINTS.e, style.height)}
        fill={style.eastWall.fill}
      />
      <polygon
        fill={topFill}
        points={iso.drawHex(1)}
      />
      <image
        x="-26"
        y="-18"
        width="52"
        height="36"
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#${clipId})`}
        opacity=".78"
        xlinkHref={assetPath}
      />
      {overlayOpacity > 0 && (
        <polygon
          fill={topFill}
          fillOpacity={overlayOpacity}
          points={iso.drawHex(1)}
        />
      )}
      <polygon
        fill="none"
        stroke={HEX_STROKE}
        strokeWidth=".05%"
        points={iso.drawHex(1)}
      />
    </g>
  )
}

function renderPitTile() {
  return (
    <g>
      <polygon
        fill="transparent"
        points={iso.drawHex(1)}
      />
    </g>
  )
}

export interface IProps {
  terrain: Terrain,
  themeId?: string,
  variant?: number,
  tileId?: string,
}

export default class Tile extends React.PureComponent<IProps, void> {
  render() {
    const themeId = this.props.themeId || 'plains'
    const palette = PALETTES[themeId] || PALETTES.plains
    const variant = this.props.variant || 0
    const tileId = this.props.tileId || `${themeId}-${variant}`

    if (this.props.terrain === Terrain.Pit) {
      return renderPitTile()
    }

    return renderRaisedTile(
      themeId,
      this.props.terrain,
      palette,
      variant,
      tileId,
    )
  }
}



// WEBPACK FOOTER //
// ./src/ui/stageView/tile.tsx